import React from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Chip, Tooltip } from '@mui/material'
import { Button } from 'src/Common/ui/button'
import { useNavigate } from 'react-router-dom'
import { useComplianceStore } from '../../Containers/Compliance/store/ComplianceContainerStore'
import { GrcTable, GrcTableBody, GrcTableCell, GrcTableContainer, GrcTableHead, GrcTableHeadRow, GrcTableRow } from '../../Common/ui/TableComponent'
import * as RoutePath from '../../Entities/RoutePath'
import { lawTypes } from 'src/Entities/Governance'
import ContentCircle from 'src/Common/ui/contentCircle'
import { getDueBasedAging } from 'src/lib/utils'

const DueTodayPopup = () => {
    const navigate = useNavigate()
    const { dueTodayCompliances, showDueTodayPopup, closeDueTodayPopup } = useComplianceStore()

    const routePath = '/GRC' + RoutePath.COMPLIANCE.MANAGE_COMPLIANCE

    const compTableColorChange = (value) => {
        switch (value) {
            case 'Nearing Due':
                return 'bg-[hsl(var(--soft-target))]'
            case 'Soft Target':
                return 'bg-[hsl(var(--soft-target))]'
            case 'Scheduled':
                return 'bg-[hsl(var(--scheduled))]'
            case 'Due':
                return 'bg-[hsl(var(--High))]'
            case 'Hard Target':
                return 'bg-[hsl(var(--High))]'
            case 'Overdue':
                return 'bg-[hsl(var(--Extreme))]'
            case 'Closed':
                return 'bg-[hsl(var(--Low))]'
            case 'Before Due':
                return 'bg-[hsl(var(--High))]'
            default:
                return 'bg-[hsl(var(--primary))]'
        }
    }

    const handleViewCompliance = (compliance, openInNewTab = false) => {
        // Only close popup when navigating in same tab
        if (!openInNewTab) {
            closeDueTodayPopup()
        }

        // Get mandate type label from typeId (1 = Mandate by Law, 2 = Mandate by Board)
        const mandateType = lawTypes?.find((item) => String(compliance.typeId) === item?.value)?.label || 'Mandate by Law'
        const regulationName = compliance.regulationName || ''
        const complianceTitle = compliance.title || ''
        const bodyType = compliance.typeId || 1
        const regulationId = compliance.regulationId || 0
        const complianceId = compliance.id || 0
        const frequency = compliance.frequencyId || 1
        const status = 1
        const cutOff = 1
        const matrix = 1

        // Calculate dueBy date (add 1 day like in ComplianceRegulatoryTable)
        const dueByDate = new Date(compliance.eventDueBy || compliance.dueBy)
        dueByDate.setDate(dueByDate.getDate() + 1)

        // Build URL
        const url = routePath + '/' + mandateType + '/' + regulationName + '/' + complianceTitle +
            '?bodyType=' + bodyType +
            '&regulation=' + 1 +
            '&regulationId=' + regulationId +
            '&complianceId=' + complianceId +
            '&activeTab=' + 0 +
            '&dueBy=' + dueByDate +
            '&status=' + status +
            '&cutoff=' + cutOff +
            '&matrix=' + matrix +
            '&view=list' +
            '&frequency=' + frequency

        if (openInNewTab) {
            // Open in new browser tab
            window.open(url, '_blank')
        } else {
            // Navigate in same tab
            navigate(url, {
                replace: true,
                state: { complianceDetails: compliance }
            })
        }
    }

    if (!showDueTodayPopup || dueTodayCompliances.length === 0) {
        return null
    }

    return (
        <Dialog
            open={showDueTodayPopup}
            onClose={closeDueTodayPopup}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    maxHeight: '80vh'
                }
            }}
        >
            <DialogTitle sx={{
                backgroundColor: 'hsl(var(--primary))',
                color: 'white',
                fontWeight: 600,
                fontSize: '1.25rem'
            }}>
                🔔 Compliances Due Today ({dueTodayCompliances.length})
            </DialogTitle>
            <DialogContent sx={{ padding: '24px', marginTop: '16px' }}>
                <p className="text-muted text-base mb-4">
                    The following compliances require your attention today. Click on a row to view details.
                </p>
                <div className="overflow-auto max-h-[50vh]">
                    <GrcTableContainer>
                        <GrcTable>
                            <GrcTableHead>
                                <GrcTableHeadRow>
                                    <GrcTableCell sx={{ minWidth: '60px', fontSize: '16px' }}>#</GrcTableCell>
                                    <GrcTableCell sx={{ minWidth: '280px', fontSize: '16px' }}>Compliance Title</GrcTableCell>
                                    <GrcTableCell align="center" sx={{ minWidth: '180px', fontSize: '16px' }}>Regulation</GrcTableCell>
                                    <GrcTableCell align="center" sx={{ minWidth: '120px', fontSize: '16px' }}>Frequency</GrcTableCell>
                                    <GrcTableCell align="center" sx={{ minWidth: '120px', fontSize: '16px' }}>Aging</GrcTableCell>
                                    <GrcTableCell align="center" sx={{ minWidth: '100px', fontSize: '16px' }}>Action</GrcTableCell>
                                </GrcTableHeadRow>
                            </GrcTableHead>
                            <GrcTableBody>
                                {dueTodayCompliances.map((compliance, index) => (
                                    <GrcTableRow
                                        key={compliance.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => handleViewCompliance(compliance)}
                                    >
                                        <GrcTableCell sx={{ fontSize: '15px' }}>
                                            <ContentCircle
                                                className={`p-2 rounded-full ${compTableColorChange(compliance.status)}`}
                                                contentClassName={'text-card text-base'}
                                                content={index + 1}
                                            />
                                        </GrcTableCell>
                                        <GrcTableCell sx={{ fontSize: '15px' }}>
                                            <div>
                                                <div className="font-medium text-base">{compliance.title}</div>
                                                <div className="flex flex-row text-sm gap-1 justify-row items-center mt-1">
                                                    <Tooltip
                                                        title={`#${compliance.referenceId || 0}`}
                                                        arrow
                                                        placement="right"
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    bgcolor: 'hsl(var(--card))',
                                                                    border: '1px solid hsl(var(--muted))',
                                                                    color: 'black',
                                                                    boxShadow: '0px 4px 4px 0px hsl(var(--shadow))'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Chip
                                                            className="whitespace-nowrap"
                                                            label={`#${compliance.referenceId || 0}`}
                                                            sx={{
                                                                backgroundColor: 'hsl(var(--primary))',
                                                                fontSize: '13px',
                                                                color: '#fff',
                                                                height: '24px',
                                                                margin: '4px 0'
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </GrcTableCell>
                                        <GrcTableCell align="center" sx={{ fontSize: '15px' }}>
                                            <span className="text-base">{compliance.regulationName || '-'}</span>
                                        </GrcTableCell>
                                        <GrcTableCell align="center" sx={{ fontSize: '15px' }}>
                                            <span className="text-base">{compliance.frequency || '-'}</span>
                                        </GrcTableCell>
                                        <GrcTableCell align="center" sx={{ fontSize: '15px' }}>
                                            <span className="text-base">
                                                {compliance.status !== 'Closed'
                                                    ? getDueBasedAging(compliance.eventDueBy)
                                                    : '-'
                                                }
                                            </span>
                                        </GrcTableCell>
                                        <GrcTableCell align="center" sx={{ fontSize: '15px' }}>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-base px-4 py-2"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleViewCompliance(compliance, true)
                                                }}
                                            >
                                                View
                                            </Button>
                                        </GrcTableCell>
                                    </GrcTableRow>
                                ))}
                            </GrcTableBody>
                        </GrcTable>
                    </GrcTableContainer>
                </div>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
                <Button variant="default" onClick={closeDueTodayPopup} className="text-base px-6 py-2">
                    Dismiss
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default DueTodayPopup
